import React, { useCallback, useEffect } from "react";
import { Transition, Dialog } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { getData, putData } from "actions";
import {
  API_URL_agent,
  API_URL_department,
  API_URL_group,
} from "constants/api";
import { agentReducers } from "redux/agentSlice";
import { departmentReducers } from "redux/departmentSlice";
import { groupReducers } from "redux/groupSlice";

const ModalChangeRole = ({ isOpen, onClose, item, callback }) => {
  const cancelButtonRef = React.useRef(null);
  const dispatch = useDispatch();

  const { getGroupResult, getDepartmentResult } = useSelector((state) => ({
    getGroupResult: state.group.getGroupResult,
    getDepartmentResult: state.department.getDepartmentResult,
  }));

  const getGroup = useCallback(
    async (params) => {
      getData(
        API_URL_group,
        params,
        { dispatch, redux: groupReducers },
        "GET_GROUP"
      );
    },
    [dispatch]
  );

  const getDepartment = useCallback(
    async (params) => {
      getData(
        API_URL_department,
        params,
        { dispatch, redux: departmentReducers },
        "GET_DEPARTMENT"
      );
    },
    [dispatch]
  );
  const formik = useFormik({
    initialValues: {
      groups: item.groups,
      primary_dpt: item.primary_dpt,
    },
    onSubmit: (values) => {
      let formData = "";
      // validasi
      if (!values.groups) {
        formik.setFieldError("groups", "Groups is required");
        return;
      }
      if (!values.primary_dpt) {
        formik.setFieldError("primary_dpt", "Primary Department is required");
        return;
      }
      formData = values;
      putData(
        `${API_URL_agent}${item.id}/`,
        formData,
        { dispatch, redux: agentReducers },
        "ADD_AGENT"
      );
      callback();
      // Reset formik state
      formik.resetForm();

      // Tutup modal
      onClose();
    },
  });

  useEffect(() => {
    const paramsGroup = `?limit=${9999999}&all=1`;
    getGroup(paramsGroup);
    getDepartment(paramsGroup);
  }, []);

  useEffect(() => {
    // Reset formik state
    formik.resetForm();
  }, [onClose]);

  return (
    <Transition.Root show={isOpen} as={React.Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={cancelButtonRef}
        onClose={onClose}
      >
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-al w-full lg:w-1/2">
                <form onSubmit={formik.handleSubmit}>
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="mt-3 sm:ml-4 sm:mt-0">
                      <div className="flex justify-between">
                        <Dialog.Title
                          as="h3"
                          className="text-base font-semibold leading-6 text-gray-900"
                        >
                          Change Role
                        </Dialog.Title>
                        <button
                          type="button"
                          className=" bg-gray-300 px-3 py-2 text-sm font-semibold text-black shadow-sm hover:bg-gray-400"
                          onClick={onClose}
                        >
                          X
                        </button>
                      </div>

                      <>
                        <div className="px-6 py-1">
                          <label htmlFor="link" className="block mb-1">
                            Assigned Group{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          {item !== undefined &&
                            getGroupResult.results &&
                            getGroupResult.results.map((item, index) => (
                              <div key={index}>
                                <label className="text-xs md:-text-sm lg:text-sm">
                                  <input
                                    id={item.name}
                                    type="checkbox"
                                    className="form-checkbox mr-2 mt-2"
                                    checked={formik.values.groups?.includes(
                                      item.id
                                    )}
                                    onChange={(e) => {
                                      const isChecked = e.target.checked;

                                      // Reset semua checkbox menjadi tidak terpilih
                                      const resetGroups = getGroupResult.results.map(
                                        (group) => group.id
                                      );

                                      if (isChecked) {
                                        formik.setFieldValue("groups", [
                                          item.id,
                                        ]);
                                      } else {
                                        formik.setFieldValue("groups", []);
                                      }
                                    }}
                                  />
                                  {item.name}
                                </label>
                                <br />
                              </div>
                            ))}
                          {formik.touched.groups && formik.errors.groups && (
                            <div className="text-red-500 dark:text-red-300 text-xs">
                              {formik.errors.groups}
                            </div>
                          )}
                        </div>

                        <div className="px-6 py-1">
                          <label htmlFor="link" className="block mb-1">
                            Department <span className="text-red-500">*</span>
                          </label>
                          <select
                            id="primary_dpt"
                            name="primary_dpt"
                            value={formik.values.primary_dpt}
                            onChange={formik.handleChange}
                            className="block w-full rounded-md border border-gray-500 bg-white py-2 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6"
                          >
                            <option value="">Select a Department</option>
                            {item !== undefined &&
                              getDepartmentResult.results &&
                              getDepartmentResult.results.map((item, index) => (
                                <option key={index} value={item.id}>
                                  {item.name}
                                </option>
                              ))}
                          </select>
                          {formik.touched.primary_dpt &&
                            formik.errors.primary_dpt && (
                              <div className="text-red-500 dark:text-red-300 text-xs">
                                {formik.errors.primary_dpt}
                              </div>
                            )}
                        </div>
                      </>
                    </div>
                  </div>
                  <div className="bg-gray-50 flex justify-between px-4 py-3 sm:px-6">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md bg-gray-300 px-3 py-2 text-sm font-semibold text-black shadow-sm hover:bg-gray-400"
                      onClick={onClose}
                    >
                      Close
                    </button>
                    <button
                      type="submit"
                      className="inline-flex justify-center rounded-md bg-sky-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-600"
                    >
                      Submit
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ModalChangeRole;
